import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Flex,
  Heading,
  IconButton,
  Image,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaShoppingCart, FaTrash } from 'react-icons/fa'
import { BsPlusSquareFill } from 'react-icons/bs'
import { useProductActions } from 'src/hooks/product/useProductActions'
import { Product, Tag } from 'src/types/types'
import { useWastePortalActions } from 'src/hooks/wastePortal/useWastePortalActions'
import { WastePortalAddButton } from './WastePortalAddButton'

const disabledStyle = {
  filter: 'grayscale(100%)',
  opacity: '0.5',
}

interface ProductCardProps {
  product: Product
  onShowDetails: (product: Product) => void
  isWastePortal?: boolean
  isUnregisteredUserOnWastePortal?: boolean
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onShowDetails,
  isWastePortal = false,
  isUnregisteredUserOnWastePortal = false,
 }) => {
  const { t } = useTranslation()
  const { addToCart, removeFromCart, isLoading, isAddedToCart } = useProductActions(product)
  const {
    addToCart: addToWastePortalCart,
    subtractFromCart: subtractFromWastePortalCart,
    count: wastePortalCartCount,
  } = useWastePortalActions(product)
  const isDisabled = isUnregisteredUserOnWastePortal
                      && !product?.tags?.map(tag => tag.name).includes('unregistered')

  const renderProductImage = () => (
    <Flex width='350px' height='200px' justifyContent='center' alignItems='center'>
      <Image
        src={product?.images[0]?.src}
        alt={product?.images[0]?.alt}
        height='120px'
        borderRadius='10px'
      />
    </Flex>
  )

  const renderProductDetails = () => (
    <Flex height='100%' direction='column' justifyContent='space-between'>
      <Flex height='100%' direction='column' justifyContent='space-between' marginBottom='1rem'>
        <Heading size='md'>{product.name}</Heading>
        <Text color='var(--color-brand)' fontSize='3xl' fontWeight='bold'>
          {euro.format(Number(product.price))}
        </Text>
      </Flex>
    </Flex>
  )

  const renderCartButton = () => {
    if (isLoading) {
      return <Spinner />
    }
    if (isAddedToCart) {
      return (
        <IconButton
          aria-label='Remove from cart'
          icon={<FaTrash />}
          backgroundColor='var(--color-brand)'
          color='white'
          size='md'
          onClick={() => removeFromCart()}
        />
      )
    }
    return (
      <IconButton
        aria-label='Add to cart'
        icon={<FaShoppingCart />}
        backgroundColor='var(--color-brand)'
        color='white'
        size='md'
        onClick={() => addToCart()}
      />
    )
  }

  const renderWastePortalButton = () => {
    if (isLoading) {
      return <Spinner />
    }
    return (
      <WastePortalAddButton
        addToCart={addToWastePortalCart}
        subtractFromCart={subtractFromWastePortalCart}
        count={wastePortalCartCount}
      />
    )
  }

  const renderFooter = () => (
    <CardFooter padding='0'>
      <Flex width='100%' direction='row' justifyContent='space-between'>
        <Button colorScheme='teal' variant='link' onClick={() => onShowDetails(product)} disabled={isDisabled}>
          {t('common.details')}
        </Button>
        {
          isWastePortal && !isDisabled
            ? renderWastePortalButton()
            : renderCartButton()
        }
      </Flex>
    </CardFooter>
  )

  if (!product) return null

  return (
    <Card key={product.id} overflow='hidden' width={{ base: '45%', sm: '90%' }} borderRadius='20px'>
      <CardBody style={ isDisabled ? disabledStyle : {}}>
        <Flex direction='row' gap='1rem'>
          {renderProductImage()}
          <Stack width='100%'>
            {renderProductDetails()}
            {renderFooter()}
          </Stack>
        </Flex>
      </CardBody>
    </Card>
  )
}

const euro = Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
})
